import React, { useState, useEffect } from 'react';
import { X, ArrowRight, CheckCircle } from 'lucide-react';
import { DEMO_MODAL } from '../constants';
import { useLanguage } from '../LanguageContext';

const EMPTY_FORM = { nom: '', email: '', societe: '', poste: '' };

function validate(form, errors) {
  const errs = {};
  if (!form.nom.trim())     errs.nom     = errors.required;
  if (!form.email.trim())   errs.email   = errors.required;
  if (!form.societe.trim()) errs.societe = errors.required;
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = errors.email;
  return errs;
}

export default function DemoModal() {
  const { lang }    = useLanguage();
  const t           = DEMO_MODAL[lang];
  const [open, setOpen]     = useState(false);
  const [form, setForm]     = useState(EMPTY_FORM);
  const [sent, setSent]     = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const handler = () => { setOpen(true); setSent(false); setForm(EMPTY_FORM); setErrors({}); };
    window.addEventListener('openDemoModal', handler);
    return () => window.removeEventListener('openDemoModal', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleClose = () => { setOpen(false); setSent(false); setForm(EMPTY_FORM); setErrors({}); };
  const handleChange = (key, value) => { setForm(prev => ({ ...prev, [key]: value })); setErrors(prev => ({ ...prev, [key]: '' })); };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate(form, t.errors);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    const bl      = t.bodyLabels;
    const subject = encodeURIComponent(`${t.subjectPrefix} — ${form.societe}`);
    const body    = encodeURIComponent(`${bl.nom} : ${form.nom}\n${bl.email} : ${form.email}\n${bl.societe} : ${form.societe}\n${bl.poste} : ${form.poste || '—'}`);
    window.open(`mailto:cockpit@pitboardsuite.com?subject=${subject}&body=${body}`, '_blank');
    setSent(true);
  };

  if (!open) return null;

  return (
    <div className="pb-modal-overlay" onClick={handleClose} data-testid="pb-demo-modal-overlay">
      <div className="pb-modal-box" onClick={e => e.stopPropagation()} data-testid="pb-demo-modal">
        <button className="pb-modal-close" onClick={handleClose} aria-label={t.closeBtn} data-testid="pb-demo-modal-close">
          <X size={18} />
        </button>

        {sent ? (
          <div className="pb-modal-success" data-testid="pb-demo-modal-success">
            <div className="pb-modal-success-icon"><CheckCircle size={44} strokeWidth={1.5} /></div>
            <h3 className="pb-modal-success-title">{t.successTitle}</h3>
            <p className="pb-modal-success-msg">{t.successMsg}</p>
            <button className="pb-btn-primary" onClick={handleClose} style={{ marginTop: 28 }} data-testid="pb-demo-success-close-btn">
              {t.closeBtn}
            </button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 28 }}>
              <div className="pb-label" style={{ marginBottom: 10 }}>{t.label}</div>
              <h3 className="pb-modal-title">{t.title}</h3>
              <p className="pb-modal-sub">{t.sub}</p>
            </div>

            <form className="pb-modal-form" onSubmit={handleSubmit} noValidate>
              {t.fields.map(f => (
                <div key={f.key} className="pb-modal-field">
                  <label className="pb-modal-label" htmlFor={`pb-field-${f.key}`}>
                    {f.label}
                    {f.required && <span className="pb-modal-req" aria-hidden="true">*</span>}
                  </label>
                  <input
                    id={`pb-field-${f.key}`}
                    type={f.type}
                    className={`pb-modal-input${errors[f.key] ? ' pb-modal-input--error' : ''}`}
                    placeholder={f.placeholder}
                    value={form[f.key]}
                    onChange={e => handleChange(f.key, e.target.value)}
                    data-testid={`pb-demo-field-${f.key}`}
                    autoComplete={f.key === 'email' ? 'email' : 'off'}
                  />
                  {errors[f.key] && <span className="pb-modal-error" role="alert">{errors[f.key]}</span>}
                </div>
              ))}

              <button type="submit" className="pb-btn-primary pb-modal-submit" data-testid="pb-demo-submit-btn">
                {t.submitBtn}
                <ArrowRight size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
